/**
 * Sprint 23.7 — `/welcome/hire` (step 3), the value moment. On mount it calls
 * the `suggest_first_hire` MCP tool with the captured intent + active theme,
 * shows a skeleton while it loads, then renders the suggestion in a
 * `<HireSuggestionCard>`.
 *
 *   - Primary ("Hire <Name>") → create_agent + update_node(persona) → mark
 *     onboarding done → /workforce (the new hire is in the roster).
 *   - "Refine first"  → /hire?prefill=…&suggested=… (full wizard, pre-filled).
 *   - "Start from scratch" → /hire (blank wizard).
 *
 * If the AI suggestion fails (or the step was deep-linked with no intent) we
 * fall back to a "start from scratch" escape so the flow never dead-ends.
 */

import { useCallback, useState, type CSSProperties } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { mcpClient } from "../../../lib/mcp-client";
import { queryKeys } from "../../../lib/query-keys";
import { showToast } from "../primitives";
import { useThemedLabels } from "../../hooks/use-themed-labels";
import { useLanguageTheme } from "../../hooks/use-language-theme";
import { useOnboardingComplete } from "../../hooks/use-onboarding";
import { HireSuggestionCard, type SuggestedAgent } from "./HireSuggestionCard";
import { StepHeading, StepLabel, StepSpacer } from "./welcome-ui";

export function WelcomeHireStep() {
  const navigate = useNavigate();
  const labels = useThemedLabels();
  const { theme } = useLanguageTheme();
  const queryClient = useQueryClient();
  const { markComplete } = useOnboardingComplete();
  const [searchParams] = useSearchParams();
  const intent = (searchParams.get("intent") ?? "").trim();
  const [busy, setBusy] = useState(false);

  const suggestionQuery = useQuery({
    queryKey: ["first-hire-suggestion", intent, theme],
    queryFn: async (): Promise<SuggestedAgent | null> => {
      const res = await mcpClient.toolsCall<{ suggestion?: SuggestedAgent }>("suggest_first_hire", {
        intent,
        theme,
      });
      if (res.isError) return null;
      return res.structuredContent?.suggestion ?? null;
    },
    enabled: intent.length > 0,
    staleTime: Infinity,
    retry: 1,
  });

  const suggestion = suggestionQuery.data ?? null;

  const handleHire = useCallback(async () => {
    if (!suggestion || busy) return;
    setBusy(true);
    try {
      const created = await mcpClient.createAgent({
        name: suggestion.name.trim(),
        description: suggestion.role.trim() || undefined,
      });
      const newConfig: Record<string, unknown> = {
        ...(created.agent_config ?? {}),
        bio: suggestion.bio.trim() ? suggestion.bio.trim() : null,
        style: suggestion.style || null,
        motivations: suggestion.motivations.trim() ? suggestion.motivations.trim() : null,
        specialties: suggestion.specialties.length > 0 ? suggestion.specialties : null,
      };
      try {
        await mcpClient.updateNode({ id: created.id, agent_config: newConfig as unknown as never });
      } catch {
        /* Soft-fail: the agent exists; persona can be backfilled from the profile. */
      }
      await markComplete().catch(() => {});
      queryClient.invalidateQueries({ queryKey: queryKeys.libraryTree() });
      queryClient.invalidateQueries({ queryKey: ["workforce-status"] });
      // Sprint 23.8 — land on the workspace home (now populated post-first-hire).
      navigate("/home");
    } catch (err) {
      setBusy(false);
      showToast(err instanceof Error ? err.message : "Couldn't create that agent.", "error");
    }
  }, [suggestion, busy, markComplete, queryClient, navigate]);

  const refineFirst = () => {
    const params = new URLSearchParams();
    if (intent) params.set("prefill", intent);
    if (suggestion) params.set("suggested", JSON.stringify(suggestion));
    void markComplete();
    navigate(`/hire?${params.toString()}`);
  };

  const startFromScratch = () => {
    void markComplete();
    navigate("/hire");
  };

  // Sprint 24.15 — templates are the recommended first hire for a new workspace.
  const browseTemplates = () => {
    void markComplete();
    navigate("/hire?start=gallery");
  };

  const loading = intent.length > 0 && suggestionQuery.isLoading;
  const failed = !loading && !suggestion;

  return (
    <>
      <StepLabel>Step 3 of 3</StepLabel>
      <StepHeading>{labels.hireConfirmTitle}</StepHeading>
      <p style={{ margin: 0, fontSize: 12, color: "var(--color-text-secondary)", lineHeight: 1.55 }}>
        Start from a proven template, or hire the agent we sketched for you below.
      </p>

      {/* Sprint 24.15 — template gallery is the recommended first hire. */}
      <button type="button" onClick={browseTemplates} style={browseTemplatesCta}>
        <i className="ti ti-template" style={{ fontSize: 15 }} aria-hidden="true" />
        Hire from a template
        <i className="ti ti-arrow-right" style={{ fontSize: 14, marginLeft: "auto" }} aria-hidden="true" />
      </button>

      <div style={dividerRow}>
        <span style={dividerLine} />
        <span style={{ fontSize: 11, color: "var(--color-text-tertiary)" }}>or a tailored suggestion</span>
        <span style={dividerLine} />
      </div>

      {loading ? (
        <SuggestionSkeleton />
      ) : failed ? (
        <div style={fallbackCard}>
          <div style={{ fontSize: 13, color: "var(--color-text-secondary)", lineHeight: 1.5 }}>
            We couldn't sketch a hire from that just yet — start from scratch and shape one together.
          </div>
        </div>
      ) : (
        suggestion && <HireSuggestionCard suggestedAgent={suggestion} />
      )}

      <StepSpacer />

      {suggestion && (
        <button type="button" onClick={() => void handleHire()} disabled={busy} style={hireButtonStyle}>
          {busy ? "Creating…" : `${labels.hireConfirmAction} ${suggestion.name}`}
        </button>
      )}

      <div style={linkRow}>
        {suggestion && (
          <button type="button" onClick={refineFirst} style={linkBtn}>
            Refine first
          </button>
        )}
        <button type="button" onClick={startFromScratch} style={{ ...linkBtn, marginLeft: suggestion ? 0 : "auto" }}>
          Start from scratch
        </button>
      </div>
    </>
  );
}

function SuggestionSkeleton() {
  return (
    <div style={{ ...fallbackCard, display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ width: 36, height: 36, borderRadius: "50%", background: "var(--color-background-tertiary)" }} />
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={{ width: 120, height: 12, borderRadius: 4, background: "var(--color-background-tertiary)" }} />
          <div style={{ width: 80, height: 10, borderRadius: 4, background: "var(--color-background-tertiary)" }} />
        </div>
      </div>
      <div style={{ width: "100%", height: 10, borderRadius: 4, background: "var(--color-background-tertiary)" }} />
      <div style={{ width: "70%", height: 10, borderRadius: 4, background: "var(--color-background-tertiary)" }} />
    </div>
  );
}

const fallbackCard: CSSProperties = {
  background: "var(--color-background-secondary)",
  borderRadius: "var(--border-radius-md)",
  padding: 14,
};

const hireButtonStyle: CSSProperties = {
  padding: "10px 18px",
  fontSize: 13,
  fontWeight: 500,
  width: "100%",
  background: "var(--color-text-primary)",
  color: "var(--color-background-primary)",
  border: "none",
  borderRadius: "var(--border-radius-md)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};

const browseTemplatesCta: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  width: "100%",
  padding: "12px 16px",
  marginTop: 12,
  fontSize: 13,
  fontWeight: 500,
  background: "var(--color-text-primary)",
  color: "var(--color-background-primary)",
  border: "none",
  borderRadius: "var(--border-radius-md)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};

const dividerRow: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  margin: "14px 0 4px",
};

const dividerLine: CSSProperties = {
  flex: 1,
  height: 1,
  background: "var(--color-border-tertiary)",
};

const linkRow: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  fontSize: 11,
  marginTop: 10,
};

const linkBtn: CSSProperties = {
  background: "transparent",
  border: "none",
  fontSize: 11,
  color: "var(--color-text-tertiary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
  padding: 0,
};
